// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import { FeedbackInitData, launchFeedback } from '@ms/inapp-feedback-loader';
import { trackError } from '../../utilities';
import { createFeedbackInitOptions } from './feedback-options';
import { ProfileTheme } from '@microsoft/vscodeedu-common';

export type InAppFeedbackErrorKind = 'initialization' | 'submit' | 'launch';

// JSON string containing metadata sent to feedback provider
export type FeedbackMetadata = string;

export interface IFeedbackLaunchParams {
    theme: ProfileTheme;
}

export interface IAppFeedbackInitData {
    hostName: string;
    appId: number;
    isProd: boolean;
    locale: string;
}

export interface IFeedbackProvider {
    element: Element;
    initialize: (initData: IAppFeedbackInitData) => Promise<void>;
    launch: (params: IFeedbackLaunchParams) => Promise<void>;
}

// Helper method to abstract the feedback form provider from the React component.
export function getFeedbackProvider(element: Element): IFeedbackProvider {
    return new OCVFeedbackProvider(element);
}

// A thin wrapper over the One Customer Voice feedback SDK. To be called from the React component.
class OCVFeedbackProvider implements IFeedbackProvider {
    private initData: FeedbackInitData | undefined;
    private appId: number | undefined;
    private isProd: boolean | undefined;

    constructor(readonly element: Element) {}

    public async initialize(initData: IAppFeedbackInitData) {
        this.initData = {
            hostName: initData.hostName,
            locale: initData.locale,
            inAppFeedbackRef: this.element,
            onInitializationError: (error: Error) => {
                trackError(error);
            },
        };
        this.appId = initData.appId;
        this.isProd = initData.isProd;

        // TODO: Prevent this from locking up the web app
        // await preloadInAppFeedbackPackage(this.initData);
    }

    public async launch(params: IFeedbackLaunchParams) {
        if (this.initData === undefined || this.appId === undefined || this.isProd === undefined) {
            trackError(new Error('Cannot launch the feedback form before it is initialized.'));
            return;
        }

        await launchFeedback(this.initData, createFeedbackInitOptions(params.theme, this.appId, this.isProd));
    }
}
